<template>
  <v-slide-y-transition mode="out-in">
    <div v-if="show">
      <!-- Desktop -->
      <div class="progress-indicator__wrapper pt-0 pb-0 d-md-flex d-none">
        <div
          v-for="(item, index) in stepsProgress"
          :key="index"
          class="progress-indicator__item"
          :class="{
            '--completed': isMilestoneCompleted(item),
            '--active': item.isActive,
          }"
        >
          <!-- Icon only if completed -->
          <div v-if="isMilestoneCompleted(item)" class="progress-indicator__icon">
            <v-icon class="progress-indicator__icon-background"> mdi-decagram </v-icon>
            <v-icon class="progress-indicator__icon-foreground"> mdi-check </v-icon>
          </div>
          <span class="progress-indicator__label">{{ item.title }}</span>
        </div>
      </div>

      <!-- Mobile: Simple step text -->
      <!-- <div class="px-3 progress-indicator__mobile d-md-none">
        {{ mobileStepText }}
      </div> -->
    </div>
  </v-slide-y-transition>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { stepV2 } from '@/enums/donationStepOrder';
import { DonationStep } from '@/interfaces/donationStep';
import { ProgressableStep } from '@/interfaces/progressableStep';
// useLabel composable is available if needed

type ProgStep = ProgressableStep & { step: number };

export default defineComponent({
  // no mixins — label translation handled inside setup
  props: {
    steps: {
      type: Array,
      default: () => [] as Array<DonationStep>,
    },
    currentStep: {
      type: Number,
      default: 1,
    },
    requestHasExternalAgent: {
      type: Boolean,
      default: false,
    },
    requestHasOnlyOneCase: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useLabel();
    const show = ref(false);

    const hasStep = (s: number) =>
      (props.steps as DonationStep[])?.some((x: DonationStep) => x.step === s);
    const hasSummaryGroup = computed(() =>
      [stepV2.SUMMARY, stepV2.PAYMENT, stepV2.SUCCESS, stepV2.PAYMENT_LINK].some((s) => hasStep(s))
    );
    const stepsProgress = computed(() => {
      const out: Array<ProgStep> = [];

      // Only add Certificate Details if it's in the available steps
      if (hasStep(stepV2.CERTIFICATE_DETAILS)) {
        out.push({
          step: stepV2.CERTIFICATE_DETAILS,
          title: t('donationGuide.progress.certificateDetails') as string,
          isComplete: props.currentStep > stepV2.CERTIFICATE_DETAILS,
          isActive: props.currentStep === stepV2.CERTIFICATE_DETAILS,
        });
      }

      out.push({
        step: stepV2.PAYMENT_AND_DELIVERY,
        title: t('donationGuide.progress.donationDetails') as string,
        isComplete: props.currentStep > stepV2.PAYMENT_AND_DELIVERY,
        isActive: props.currentStep === stepV2.PAYMENT_AND_DELIVERY,
      });

      out.push({
        step: stepV2.SUMMARY,
        title: t('donationGuide.progress.checkout') as string,
        isComplete: props.currentStep > stepV2.SUMMARY,
        isActive: props.currentStep === stepV2.SUMMARY,
      });

      if (hasSummaryGroup.value) {
        out.push({
          step: stepV2.SUCCESS,
          title: t('donationGuide.progress.review') as string,
          isComplete: false,
          isActive: props.currentStep >= stepV2.SUCCESS && props.currentStep <= stepV2.PAYMENT_LINK,
        });
      }

      return out;
    });
    const mobileStepText = computed(() => {
      const total: number = Math.max(stepsProgress.value.length, 1);
      let stepIndex: number = stepsProgress.value.findIndex((s) => s.isActive);
      // count how many milestones are at or before the current step
      if (stepIndex < 0) {
        const passed = stepsProgress.value.filter((s) => s.step <= props.currentStep).length;
        stepIndex = Math.min(Math.max(passed - 1, 0), total - 1);
      }
      const current = stepIndex + 1;
      return t('donationGuide.progress.mobileText', { current: current, total: total });
    });

    onMounted(() => {
      show.value = true;
    });

    const isMilestoneCompleted = (step: ProgStep): boolean => {
      return step.isComplete || props.currentStep === stepV2.SUCCESS;
    };

    return {
      t,
      show,
      hasStep,
      hasSummaryGroup,
      stepsProgress,
      mobileStepText,
      isMilestoneCompleted,
    };
  },
});
</script>

<style scoped lang="scss">
.progress-indicator {
  &__wrapper {
    position: relative;
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    font-size: var(--text-size-base);
    letter-spacing: 0.25px;
    color: #6b7280;
    padding: 16px 0px !important;
  }

  &__wrapper::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
    height: 1px;
    background: #eee;
  }

  &__item {
    display: flex;
    flex-direction: row;
    gap: 4px;
    padding: 4px 12px;
    background: white;
    z-index: 1;

    &.--active {
      .progress-indicator__label {
        color: black;
      }
    }

    &.--completed {
      font-weight: 600;
      color: var(--primary-color);

      .progress-indicator__icon-background,
      .progress-indicator__label {
        color: var(--primary-color);
      }

      .progress-indicator__icon-foreground {
        color: white;
      }
    }
  }

  &__label {
    font-size: var(--text-size-base);
    color: var(--text-muted);
    font-weight: 600;
    margin-bottom: 1px;
    margin-left: 2px;
    letter-spacing: 0.0025em;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 16px;
    margin-bottom: 2px;
  }

  &__icon-background {
    color: var(--primary-color);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    font-size: 18px;
  }

  &__icon-foreground {
    z-index: 2;
    font-size: 10px;
  }
}
</style>
